import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { APPLICATION_URL, wallCopy, wallItems } from "../../content";
import type { WallItem } from "../../content";
import { useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";
import { Button } from "../ui/Button";

const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * Layout gate for the desk. The draggable desk only exists at md and up;
 * below that we render a scroll-snap row with no drag props mounted at all,
 * so touch scrolling never fights the drag gesture.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    setMatches(list.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

/**
 * Hardcoded scatter for the desk, index-aligned with `wallItems`.
 * Left percentages are conservative so fixed-width cards stay inside the
 * desk (and clear of each other) from ~688px desk width up to 1320px.
 */
const deskScatter: { left: string; top: string }[] = [
  { left: "4%", top: "8%" }, // idea: side project
  { left: "26%", top: "40%" }, // snippet: agent.ts
  { left: "6%", top: "63%" }, // sticky: demo day
  { left: "52%", top: "6%" }, // diagram
  { left: "64%", top: "46%" }, // idea: prototype no. 4
  { left: "40%", top: "73%" }, // sticky: note to self
  { left: "68%", top: "78%" }, // mystery
];

/** users, magic, revenue: the plan every desk has pinned somewhere. */
function DiagramDoodle() {
  return (
    <svg viewBox="0 0 216 92" className="block w-full" aria-hidden focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.6" className="text-ink">
        <rect x="4" y="32" width="56" height="26" />
        <rect x="80" y="32" width="56" height="26" />
        <rect x="156" y="32" width="56" height="26" />
        {/* arrows between the boxes */}
        <path d="M60 45h14M136 45h14" strokeLinecap="round" />
      </g>
      <path d="M72 41.5l8 3.5-8 3.5Z" className="fill-ink" />
      <path d="M148 41.5l8 3.5-8 3.5Z" className="fill-ink" />
      {/* the honest bit, circled in signal like a marker pen */}
      <ellipse
        cx="108"
        cy="45"
        rx="39"
        ry="20"
        transform="rotate(-4 108 45)"
        fill="none"
        className="stroke-signal"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="118 8"
      />
      <g className="fill-ink font-mono" fontSize="9.5" textAnchor="middle">
        <text x="32" y="48.5">users</text>
        <text x="108" y="48.5">magic</text>
        <text x="184" y="48.5">revenue</text>
      </g>
    </svg>
  );
}

/** Card chrome per kind. Widths come from the spec, radius system is strict. */
function cardChrome(item: WallItem): string {
  switch (item.kind) {
    case "sticky":
      return "w-56 rounded-md border-2 border-ink bg-manila p-5 shadow-card";
    case "snippet":
      return "w-64 rounded-md border-2 border-ink bg-foam p-4 shadow-card";
    case "diagram":
      return "w-64 rounded-md border-2 border-ink bg-foam p-4 shadow-card";
    case "idea":
      return "w-60 rounded-md border-2 border-ink bg-foam p-5 shadow-card";
    case "mystery":
      return "w-52 rounded-md border-2 border-dashed border-ink bg-paper p-5 shadow-card";
  }
}

function WallCardContent({ item }: { item: WallItem }) {
  switch (item.kind) {
    case "sticky":
      return <p className="font-medium text-ink">{item.text}</p>;
    case "snippet":
      return (
        <>
          <p className="font-mono text-xs text-muted">{item.title}</p>
          <pre className="mt-2 overflow-hidden font-mono text-xs leading-relaxed text-ink">
            {item.code}
          </pre>
        </>
      );
    case "diagram":
      return (
        <>
          <DiagramDoodle />
          {/* the diagram's own annotation; kept tiny because the drag hint also exists */}
          <p
            aria-hidden
            className="hand-note mt-1 rotate-[-2deg] text-center text-ink-soft"
            style={{ fontSize: "1rem" }}
          >
            figure this bit out
          </p>
        </>
      );
    case "idea":
      return (
        <>
          <p className="font-semibold text-ink">{item.title}</p>
          <p className="mt-1.5 text-sm text-ink-soft">{item.text}</p>
        </>
      );
    case "mystery":
      return <p className="font-mono text-sm text-ink-soft">{wallCopy.mystery}</p>;
  }
}

export function BuildWall() {
  const reduce = useReducedMotion();
  const { stamp } = useGame();
  const hasDesk = useMediaQuery("(min-width: 48rem)");
  const deskRef = useRef<HTMLDivElement>(null);

  const reveal = (index: number) => ({
    initial: reduce ? false : { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, delay: index * 0.06, ease: EASE },
  });

  return (
    <section id="wall" className="bg-paper-deep py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <motion.div {...reveal(0)}>
          <Waypoint station="wall" title={wallCopy.title} sub={wallCopy.sub} />
        </motion.div>

        {hasDesk ? (
          <>
            <div
              ref={deskRef}
              className="chart-grid relative mt-12 h-[38rem] overflow-hidden rounded-md border-2 border-ink bg-paper"
            >
              {wallItems.map((item, i) => {
                const spot = deskScatter[i] ?? { left: "4%", top: "8%" };
                return (
                  <motion.div
                    key={`${item.kind}-${i}`}
                    drag
                    dragConstraints={deskRef}
                    dragMomentum={false}
                    dragElastic={0.08}
                    onDragStart={() => stamp("wall")}
                    whileDrag={{ scale: 1.04, zIndex: 30 }}
                    {...reveal(i)}
                    style={{ left: spot.left, top: spot.top, rotate: item.rotate }}
                    className={`absolute cursor-grab touch-none select-none active:cursor-grabbing ${cardChrome(item)}`}
                  >
                    <WallCardContent item={item} />
                  </motion.div>
                );
              })}
            </div>
            <p className="hand-note mt-3 rotate-[-1.5deg] pr-2 text-right text-ink-soft" style={{ fontSize: "1.1rem" }}>
              {wallCopy.dragHint}
            </p>
          </>
        ) : (
          /* Mobile desk: a swipeable snap row, no drag. Focusable so keyboard users can scroll it. */
          <div
            role="region"
            aria-label={wallCopy.title}
            tabIndex={0}
            onScroll={() => stamp("wall")}
            className="-mx-5 mt-10 flex snap-x snap-mandatory items-center gap-4 overflow-x-auto px-5 py-4"
          >
            {wallItems.map((item, i) => (
              <motion.div
                key={`${item.kind}-${i}`}
                {...reveal(Math.min(i, 2))}
                style={{ rotate: item.rotate }}
                className={`shrink-0 snap-center ${cardChrome(item)}`}
              >
                <WallCardContent item={item} />
              </motion.div>
            ))}
          </div>
        )}

        <motion.div {...reveal(2)} className="mt-12">
          <Button href={APPLICATION_URL}>{wallCopy.cta}</Button>
        </motion.div>
      </div>
    </section>
  );
}
